import {
  Directive,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { distinctUntilChanged, Observable, of, Subject, takeUntil } from 'rxjs';
import { LoadingStatesService } from '../service/loading-states.service';
import { ReportInputsService } from '../service/report-inputs.service';

@Directive()
export abstract class SelfInspectionBaseClassComponent<T>
  implements OnInit, OnDestroy
{
  @Output() undoSelfInspection = new EventEmitter();
  @Output() markAsNA = new EventEmitter<T>();
  @Output() uploadFiles = new EventEmitter<Event>();

  private readonly cancelSub$ = new Subject<void>();

  abstract get selfInspectionId(): string;

  isLoading$ = new Observable<boolean>();

  constructor(
    private readonly reportInputsService: ReportInputsService,
    private readonly loadingStatesService: LoadingStatesService,
  ) {}

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.subscribeToMarkAsNA();
    this.subscribeToUndoSelfInspection();
    this.subscribeToUploadFiles();
  }

  ngOnDestroy(): void {
    this.cancelSub$.next();
  }

  setIsLoadingTrue() {
    this.loadingStatesService.setIsLoadingValue(this.selfInspectionId, true);
  }

  subscribeToLoadingState() {
    this.loadingStatesService.isLoadingValue$
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((values) => {
        this.isLoading$ = of(values.get(this.selfInspectionId) || false);
      });
  }

  subscribeToMarkAsNA() {
    this.reportInputsService.markAsNAValue$
      .pipe(distinctUntilChanged(), takeUntil(this.cancelSub$))
      .subscribe((markAsNAValues: { [id: string]: string }) => {
        if (markAsNAValues[this.selfInspectionId]) {
          this.setIsLoadingTrue();
          this.markAsNA.emit({
            isNotApplicableComment: markAsNAValues[this.selfInspectionId],
          } as T);
        }
      });
  }

  subscribeToUndoSelfInspection() {
    this.reportInputsService.undoSelfInspection$
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((res) => {
        if (res === this.selfInspectionId) {
          this.setIsLoadingTrue();
          this.undoSelfInspection.emit();
        }
      });
  }

  subscribeToUploadFiles() {
    this.reportInputsService.uploadFiles$
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((res) => {
        const [id, event] = Object.entries(res)[0];
        if (id === this.selfInspectionId) {
          this.setIsLoadingTrue();
          this.uploadFiles.emit(event);
        }
      });
  }
}
